import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {formatDateTime} from '../utils/dateTime';

/**
 * Админ-управление глобальным kill-switch AI-контура (CTHS-26).
 * При активном kill-switch весь AI отключён, операции деградируют в ручной режим.
 */
interface AiKillSwitchDto {
    active: boolean;
    updatedAt?: string;
    updatedBy?: string;
}

const AiKillSwitch: React.FC = () => {
    const [state, setState] = useState<AiKillSwitchDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchState = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<AiKillSwitchDto>('/admin/ai/kill-switch');
            setState(res.data);
        } catch (e) {
            console.error('Error fetching AI kill-switch:', e);
            setError('Не удалось загрузить состояние kill-switch');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchState();
    }, [fetchState]);

    const toggle = async () => {
        if (!state) return;
        const next = !state.active;
        setSaving(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.put<AiKillSwitchDto>('/admin/ai/kill-switch', {active: next});
            setState(res.data);
        } catch (e) {
            console.error('Error toggling AI kill-switch:', e);
            setError('Не удалось переключить kill-switch');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Kill-switch AI-контура
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Глобальное аварийное отключение всех AI-вызовов. При активации новое AI-распознавание
                блокируется, пользователи добавляют вещи вручную.
            </p>
            {loading ? (
                <p className="text-sm text-gray-500">Загрузка...</p>
            ) : (
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                            AI {state?.active ? 'отключён' : 'включён'}
                        </span>
                        {state?.updatedBy && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Последнее изменение: {state.updatedBy}
                                {state.updatedAt ? ` (${formatDateTime(state.updatedAt)})` : ''}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={toggle}
                        disabled={saving}
                        className={state?.active ? 'btn-primary' : 'btn-danger'}
                    >
                        {saving ? '...' : state?.active ? 'Включить AI' : 'Отключить AI'}
                    </button>
                </div>
            )}
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
        </div>
    );
};

export default AiKillSwitch;
